<template>
  <div>
    <!-- Comments List Card -->
    <b-card class="comments-list-card">
      <!-- Gradient Line Wrapper -->
      <div class="gradient-line-wrapper"></div>
      <!-- Table Header -->
      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0 comment-title">Comments</h5>
          <small class="text-muted">Manage and review comments</small>
        </div>
      </div>

      <!-- Filter Row -->
      <b-row class="align-items-center filter-row-single-line mb-2">
        <b-col v-if="showQuoteField" xl="3" lg="3" md="5" sm="12" cols="12">
          <b-form-input
            v-model="quoteSearchQuery"
            placeholder="Search subjects..."
            class="filter-control"
            @input="onSearchChange"
            debounce="500"
          />
        </b-col>
        <!-- Search Input -->
        <b-col
          :xl="showQuoteField ? 3 : 6"
          :lg="showQuoteField ? 3 : 6"
          md="5"
          sm="12"
          cols="12"
        >
          <b-form-input
            v-model="searchQuery"
            placeholder="Search comments..."
            class="filter-control"
            @input="onSearchChange"
            debounce="500"
          />
        </b-col>
        <!-- Action Buttons -->
        <b-col
          :xl="showQuoteField ? 6 : 6"
          :lg="showQuoteField ? 6 : 6"
          md="7"
          sm="12"
          cols="12"
          class="text-right"
        >
          <div
            class="d-flex gap-2 align-items-center justify-content-end flex-wrap"
          >
            <!-- <b-button
              @click="exportComments"
              variant="outline-success"
              class="action-btn-filter"
              :disabled="exporting"
            >
              <b-spinner v-if="exporting" small class="mr-1" />
              <feather-icon v-else icon="DownloadIcon" size="16" class="mr-1" />
              Export
            </b-button>
            <b-button
              v-if="rights.add"
              @click="openImportModal"
              variant="outline-primary"
              class="action-btn-filter"
              :disabled="importing"
            >
              <b-spinner v-if="importing" small class="mr-1" />
              <feather-icon v-else icon="UploadIcon" size="16" class="mr-1" />
              Import
            </b-button> -->
            <b-button
              v-if="rights.add"
              @click="openAddModal"
              variant="gradient-primary"
              class="action-btn-filter"
              :disabled="saving"
            >
              <b-spinner v-if="saving" small class="mr-1" />
              <feather-icon v-else icon="PlusIcon" size="16" class="mr-1" />
              Add Comment
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!-- Pagination Controls - Top (Compact Single Line) -->
      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <!-- Page Size Dropdown -->
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              :key="'page-size-' + perPage"
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
              @option:selected="closePageSizeDropdown"
              ref="pageSizeSelect"
            />
          </div>
        </div>

        <!-- Count Display in Middle -->
        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <!-- Pagination -->
        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact"
            @change="onPageChange"
          />
        </div>
      </div>

      <!-- Data Grid -->
      <b-table
        class="comments-table"
        :items="comments"
        :fields="fields"
        :busy="tableBusy"
        responsive
        hover
        show-empty
        :empty-text="tableEmptyText"
      >
        <template #table-busy>
          <tbody>
            <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
              <td
                v-for="(field, index) in fields"
                :key="'skeleton-cell-' + index"
                class="skeleton-cell"
              >
                <div class="skeleton-loader"></div>
              </td>
            </tr>
          </tbody>
        </template>

        <template #cell(sno)="{ index }">
          <span class="text-muted">{{
            (currentPage - 1) * perPage + index + 1
          }}</span>
        </template>

        <template #cell(exam)="{ item }">
          <div v-if="item.examID > 0">
            {{ getExamName(item.examID) }}
          </div>
        </template>

        <template #cell(quote)="{ item }">
          <span>{{ item.quote || "—" }}<br />{{ item.gender }}</span>
        </template>

        <template #cell(remarks)="{ item }">
          <span>{{ item.remarks || "N/A" }}</span>
        </template>

        <template #cell(actions)="{ item }">
          <div class="d-flex align-items-center justify-content-end">
            <b-button
              v-if="rights.edit"
              variant="gradient-primary"
              v-b-tooltip.hover.left
              title="Edit"
              size="sm"
              class="action-btn-table mr-1"
              @click="editComment(item)"
              :disabled="editLoading === item.id || deleteLoading === item.id"
            >
              <b-spinner v-if="editLoading === item.id" small type="grow" />
              <feather-icon v-else icon="EditIcon" size="16" />
            </b-button>
            <b-button
              v-if="rights.delete"
              variant="outline-danger"
              v-b-tooltip.hover.left
              title="Delete"
              size="sm"
              class="action-btn-outline"
              @click="deleteComment(item)"
              :disabled="editLoading === item.id || deleteLoading === item.id"
            >
              <b-spinner v-if="deleteLoading === item.id" small type="grow" />
              <feather-icon v-else icon="Trash2Icon" size="16" />
            </b-button>
          </div>
        </template>
      </b-table>
    </b-card>

    <!-- Add/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="isEdit ? 'Edit Comment' : 'Add Comment'"
      @hidden="resetForm"
      centered
      size="lg"
      no-close-on-backdrop
      class="remarks-modal"
    >
      <b-form @submit.prevent>
        <b-form-group
          :label="$t('Exam')"
          :invalid-feedback="$t('Exam is required.')"
          ref="exam"
        >
          <v-select
            v-model="form.examID"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="examOptions"
            :reduce="(val) => val.id"
            label="examType"
            :clearable="false"
            :disabled="saving || examLoading"
            :placeholder="$t('Select exam')"
          />
        </b-form-group>
        <b-row>
          <b-col md="6">
            <b-form-group
              :label="$t('Subject')"
              :invalid-feedback="$t('Subject is required.')"
              ref="subject"
            >
              <v-select
                v-model="form.quote"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="typeOptions"
                :reduce="(val) => val.value"
                label="text"
                :disabled="saving || subLoading"
                :clearable="false"
                :placeholder="$t('Select subject')"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              :label="$t('Gender')"
              :invalid-feedback="$t('Gender is required')"
              ref="gender"
            >
              <v-select
                v-model="form.gender"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="genders"
                :clearable="false"
                :disabled="rankList.includes(form.quote)"
                :placeholder="$t('Select gender')"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group label="Comment" label-for="remarks">
          <b-form-textarea
            id="remarks"
            v-model="form.remarks"
            placeholder="Enter comment"
            rows="8"
            :disabled="saving"
            :style="{
              direction: urduSubs.includes(form.quote) ? 'rtl' : 'ltr',
            }"
            class="modern-textarea"
          />
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="showModal = false"
          :disabled="saving"
          class="btn-modern-cancel"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="saveComments"
          :disabled="saving || !isFormValid"
          class="btn-modern-primary"
        >
          <b-spinner v-if="saving" small class="mr-2" />
          <feather-icon v-else icon="SaveIcon" size="16" class="mr-2" />
          {{ saving ? "Saving..." : isEdit ? "Update" : "Save" }}
        </b-button>
      </template>
    </b-modal>

    <!-- Import Modal -->
    <b-modal
      v-model="showImportModal"
      title="Import Comments"
      @hidden="resetImportModal"
      centered
      size="lg"
      no-close-on-backdrop
      class="remarks-modal import-modal"
    >
      <b-form @submit.prevent>
        <!-- Import Instructions -->
        <div class="import-instructions-wrapper mb-3">
          <div class="instruction-card instruction-new mb-2">
            <div class="instruction-header">
              <feather-icon
                icon="FilePlusIcon"
                size="16"
                class="instruction-icon"
              />
              <div class="instruction-content">
                <div class="instruction-title">
                  <strong>New Import</strong>
                  <feather-icon
                    icon="HelpCircleIcon"
                    size="12"
                    class="ml-1 tooltip-icon"
                    v-b-tooltip.html.top
                    :title="getColumnTooltip()"
                  />
                </div>
                <div class="instruction-text">
                  <template v-if="showQuoteField">
                    Include <code>quote</code> and <code>remarks</code> columns.
                    Each row creates a new comment.
                  </template>
                  <template v-else>
                    Include only <code>remarks</code> column. Each row creates a
                    new comment.
                  </template>
                </div>
              </div>
            </div>
          </div>

          <div class="instruction-card instruction-update">
            <div class="instruction-header">
              <feather-icon
                icon="EditIcon"
                size="16"
                class="instruction-icon"
              />
              <div class="instruction-content">
                <div class="instruction-title">
                  <strong>Bulk Update</strong>
                  <feather-icon
                    icon="InfoIcon"
                    size="12"
                    class="ml-1 tooltip-icon"
                    v-b-tooltip.html.top
                    title="<div style='text-align: left; padding: 0.5rem;'><strong>Quick Guide:</strong><br/>1. Click <strong>Export</strong> to download existing comments<br/>2. Edit the <code>remarks</code> column<br/>3. Keep <code>id</code> column unchanged<br/>4. Import back - updates automatically</div>"
                  />
                </div>
                <div class="instruction-text">
                  <template v-if="showQuoteField">
                    Include <code>id</code>, <code>quote</code>, and
                    <code>remarks</code> columns.
                  </template>
                  <template v-else>
                    Include <code>id</code> and <code>remarks</code> columns.
                  </template>
                  <span class="instruction-detail"
                    >Use existing <code>id</code> to update, or leave empty/0
                    for new.</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- File Upload -->
        <b-form-group
          label="Select Excel File"
          label-for="importFile"
          class="mb-2"
        >
          <b-form-file
            id="importFile"
            ref="importFile"
            v-model="importFile"
            accept=".xlsx,.xls"
            placeholder="Choose file or drop it here..."
            drop-placeholder="Drop file here..."
            @change="onFileSelected"
            :state="importFileState"
            class="modern-file-input"
          />
          <small class="form-text text-muted">
            <feather-icon icon="FileIcon" size="12" class="mr-1" />
            Supported: .xlsx, .xls
          </small>
        </b-form-group>

        <!-- Preview Section -->
        <div v-if="importPreview.length > 0" class="import-preview mt-2">
          <div class="d-flex align-items-center justify-content-between mb-1">
            <small class="font-weight-bold text-muted">
              <feather-icon icon="EyeIcon" size="14" class="mr-1" />
              Preview ({{ importPreview.length
              }}{{
                totalImportRecords > importPreview.length
                  ? ` of ${totalImportRecords}`
                  : ""
              }})
            </small>
            <b-badge variant="info" class="badge-sm">{{
              importMode === "new"
                ? "All New"
                : `${newCount} New, ${updateCount} Update`
            }}</b-badge>
          </div>
          <div class="preview-table-wrapper">
            <b-table
              :items="importPreview"
              :fields="importPreviewFields"
              small
              striped
              hover
              class="preview-table"
            >
              <template #cell(id)="{ item }">
                <b-badge
                  :variant="item.id > 0 ? 'warning' : 'success'"
                  class="type-badge"
                >
                  <feather-icon
                    :icon="item.id > 0 ? 'EditIcon' : 'PlusIcon'"
                    size="12"
                    class="mr-1"
                  />
                  {{ item.id > 0 ? `Update #${item.id}` : "New" }}
                </b-badge>
              </template>
              <template #cell(quote)="{ item }">
                <span class="remarks-preview">{{ item.quote || "—" }}</span>
              </template>
              <template #cell(remarks)="{ item }">
                <span class="remarks-preview">{{ item.remarks || "N/A" }}</span>
              </template>
            </b-table>
          </div>
        </div>
      </b-form>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="showImportModal = false"
          :disabled="importing"
          class="btn-modern-cancel"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="processImport"
          :disabled="importing || !importFile || importPreview.length === 0"
          class="btn-modern-primary"
        >
          <b-spinner v-if="importing" small class="mr-2" />
          <feather-icon v-else icon="UploadIcon" size="16" class="mr-2" />
          {{ importing ? "Importing..." : "Import" }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BPagination,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormFile,
  BSpinner,
  BModal,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions } from "vuex";
import readXlsxFile from "read-excel-file";
import * as XLSX from "xlsx";

export default {
  name: "CommentBank",
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BPagination,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormFile,
    BSpinner,
    BModal,
    vSelect,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      comments: [],
      dataLoading: false,
      saving: false,
      rights: {},
      commentsCache: {},
      cacheTTL: 2 * 60 * 1000, // 2 minutes
      currentRequestKey: null,
      editLoading: null,
      deleteLoading: null,
      currentPage: 1,
      perPage: 10,
      totalCount: 0,
      searchQuery: "",
      quoteSearchQuery: "",
      searchTimeout: null,
      showModal: false,
      isEdit: false,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "40", value: 40 },
        { label: "80", value: 80 },
        { label: "100", value: 100 },
        { label: "200", value: 200 },
      ],
      form: {
        id: 0,
        remarks: "",
        quote: "",
      },
      showImportModal: false,
      importFile: null,
      importFileState: null,
      importing: false,
      exporting: false,
      importMode: "new", // 'new' or 'update'
      importPreview: [],
      totalImportRecords: 0,
      newCount: 0,
      updateCount: 0,
      subLoading: false,
      allSubjects: [],
      typeOptions: [
        { text: "None", value: "none" },
        { text: "1st", value: "1st" },
        { text: "2nd", value: "2nd" },
        { text: "3rd", value: "3rd" },
      ],
      rankList: ["1st", "2nd", "3rd"],
      genders: ["Male", "Female"],
      examLoading: false,
      examOptions: [],
      urduSubs: [
        "Urdu",
        "Islamiat",
        "Islamiyat",
        "Sindhi",
        "Nazra-E-Quran",
        "Tajweed e Qur'an",
        "Arabic",
      ],
    };
  },
  computed: {
    reportDomain() {
      return (this.$store.state.report || "").replace(/_/g, "");
    },
    showQuoteField() {
      return true;
    },
    fields() {
      const cols = [
        {
          key: "sno",
          label: "S.No",
          sortable: false,
          thStyle: { width: "80px", textAlign: "center" },
          tdClass: "text-center",
        },
        {
          key: "exam",
          label: "Exam",
          sortable: false,
          tdClass: "comment-cell",
        },
      ];

      if (this.showQuoteField) {
        cols.push({
          key: "quote",
          label: "Subject",
          sortable: false,
          thStyle: { width: "auto", minWidth: "220px" },
          tdClass: "comment-cell",
        });
      }

      cols.push(
        {
          key: "remarks",
          label: "Comment",
          sortable: false,
          thStyle: { width: "auto", minWidth: "400px" },
          tdClass: "comment-cell",
        },
        {
          key: "actions",
          label: "Actions",
          sortable: false,
          thStyle: { width: "150px", textAlign: "center" },
          tdClass: "actions-cell text-center",
        }
      );

      return cols;
    },
    importPreviewFields() {
      const cols = [
        {
          key: "id",
          label: "Type",
          sortable: false,
          thStyle: { width: "120px" },
        },
      ];

      if (this.showQuoteField) {
        cols.push({
          key: "quote",
          label: "Quote",
          sortable: false,
          thStyle: { width: "180px" },
        });
      }

      cols.push({ key: "remarks", label: "Remarks", sortable: false });
      return cols;
    },
    isFormValid() {
      return (
        this.form.examID > 0 &&
        this.form.remarks &&
        this.form.remarks.trim().length > 0
      );
    },
    tableBusy() {
      return this.dataLoading;
    },
    tableEmptyText() {
      return this.tableBusy ? "" : "No comments found";
    },
    displayedRange() {
      if (this.totalCount === 0) {
        return { start: 0, end: 0 };
      }
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
  },
  created() {
    // const rightsForRoute = this.$store.state.rights?.[this.$route.name];
    // if (!rightsForRoute) {
    //   this.$router.replace({ name: "misc-not-authorized" });
    //   return;
    // }
    this.rights = { view: true, add: true, edit: true, delete: true };
    this.LoadExams();
    this.LoadSubjects();
    this.loadComments();
  },

  mounted() {
    // Add click-outside handler to close v-select dropdowns
    this.handleClickOutside = (event) => {
      // Check if click is on any v-select component or its dropdown menu
      const clickedElement = event.target;
      const isVSelectClick =
        clickedElement.closest(".v-select") ||
        clickedElement.closest(".vs__dropdown-menu") ||
        clickedElement.closest(".vs__dropdown-option") ||
        clickedElement.closest(".vs__dropdown-toggle") ||
        clickedElement.closest(".vs__actions") ||
        clickedElement.closest(".vs__selected-options");

      // If clicked outside, close all open dropdowns
      if (!isVSelectClick) {
        // Use Vue component refs if available to close dropdowns properly
        const vSelectRefs = ["pageSizeSelect"];
        vSelectRefs.forEach((refName) => {
          if (this.$refs[refName] && this.$refs[refName].$el) {
            const toggle = this.$refs[refName].$el.querySelector(
              ".vs__dropdown-toggle"
            );
            if (toggle) {
              toggle.blur();
            }
          }
        });

        // Blur all focused v-select toggles
        const allToggles = document.querySelectorAll(".vs__dropdown-toggle");
        allToggles.forEach((toggle) => {
          if (document.activeElement === toggle) {
            toggle.blur();
          }
        });

        // Close all visible dropdown menus by finding their parent v-select components
        const dropdownMenus = document.querySelectorAll(".vs__dropdown-menu");
        dropdownMenus.forEach((menu) => {
          const style = window.getComputedStyle(menu);
          if (style.display !== "none" && style.visibility !== "hidden") {
            // Since menu is appended to body, we need to find v-select by checking all v-selects
            const allVSelects = document.querySelectorAll(".v-select");
            allVSelects.forEach((vSelect) => {
              const toggle = vSelect.querySelector(".vs__dropdown-toggle");
              if (toggle && document.activeElement === toggle) {
                toggle.blur();
              }
            });
          }
        });

        // Force blur on document active element if it's a v-select toggle
        if (
          document.activeElement &&
          document.activeElement.classList.contains("vs__dropdown-toggle")
        ) {
          document.activeElement.blur();
        }
      }
    };

    // Add event listeners for both click and mousedown
    document.addEventListener("click", this.handleClickOutside, true);
    document.addEventListener("mousedown", this.handleClickOutside, true);
  },

  beforeDestroy() {
    // Remove event listeners
    if (this.handleClickOutside) {
      document.removeEventListener("click", this.handleClickOutside, true);
      document.removeEventListener("mousedown", this.handleClickOutside, true);
    }
  },
  methods: {
    ...mapActions({ get: "get", post: "post", delete: "delete", put: "put" }),

    getExamName(id) {
      return this.examOptions.find((el) => el.id == id)?.examType || "";
    },
    async LoadExams() {
      this.examLoading = true;
      var obj = {
        url:
          this.$store.state.domain +
          "ExamTypes/LoadData?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&status=active&page=1&pageSize=1000",
        token: this.$store.state.userData.token,
      };
      this.examOptions = await this.get(obj);
      this.examLoading = false;
    },
    async LoadSubjects() {
      this.subLoading = true;
      var obj = {
        url:
          this.$store.state.domain +
          "Subjects?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.allSubjects = await this.get(obj);
      this.subLoading = false;
      if (this.allSubjects.length > 0) {
        const subs = this.allSubjects.map((el) => ({
          text: el.subject,
          value: el.subject,
        }));
        this.typeOptions = [...this.typeOptions, ...subs];
      }
    },

    async loadComments() {
      const search = this.searchQuery?.trim() || "";
      const quoteSearch = this.quoteSearchQuery?.trim() || "";
      const cacheKey = `${this.currentPage}|${this.perPage}|${search}|${quoteSearch}`;

      const cached = this.commentsCache[cacheKey];
      if (cached && Date.now() - cached.timestamp < this.cacheTTL) {
        this.comments = cached.data;
        this.totalCount = cached.totalCount;
        return;
      }

      this.dataLoading = true;
      this.currentRequestKey = cacheKey;
      try {
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          campusID: this.$store.state.userData.cId,
          page: this.currentPage,
          pageSize: this.perPage,
        });

        if (search) {
          params.append("search", search);
        }

        if (this.showQuoteField && quoteSearch) {
          params.append("quote", quoteSearch);
        }

        const obj = {
          url:
            this.$store.state.domain +
            "RemarksExtra/GetByPagination?" +
            params.toString(),
          token: this.$store.state.userData.token,
          dashboard: true,
        };
        const res = await this.get(obj);

        if (this.currentRequestKey !== cacheKey) {
          return;
        }

        if (res && res.status === "success" && Array.isArray(res.data)) {
          this.comments = res.data;
          this.totalCount = res.totalCount || 0;
        } else if (res && Array.isArray(res.data)) {
          this.comments = res.data;
          this.totalCount = res.total || res.totalCount || 0;
        } else {
          this.comments = [];
          this.totalCount = 0;
        }

        this.commentsCache[cacheKey] = {
          data: this.comments,
          totalCount: this.totalCount,
          timestamp: Date.now(),
        };
      } catch (error) {
        this.$bvToast.toast("Failed to load comments", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
        this.comments = [];
        this.totalCount = 0;
      } finally {
        this.dataLoading = false;
      }
    },
    onSearchChange() {
      // Debounce search
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadComments();
      }, 500);
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadComments();
    },
    onPageSizeChange() {
      this.currentPage = 1;
      this.loadComments();
    },
    closePageSizeDropdown() {
      this.$nextTick(() => {
        if (this.$refs.pageSizeSelect) {
          const toggle = this.$refs.pageSizeSelect.$el?.querySelector(
            ".vs__dropdown-toggle"
          );
          if (toggle) {
            toggle.blur();
            setTimeout(() => {
              document.body.click();
            }, 100);
          }
        }
      });
    },
    openAddModal() {
      this.isEdit = false;
      this.form = {
        id: 0,
        remarks: "",
        quote: "none",
        examID: 0,
        examIDStr: "",
        gender: "",
      };
      this.showModal = true;
    },
    editComment(item) {
      this.editLoading = item.id;
      this.isEdit = true;
      this.form = {
        id: item.id,
        remarks: item.remarks || "",
        quote: item.quote || "",
        examID: item.examID,
        examIDStr: item.examIDStr || "",
        gender: item.gender || "",
      };
      this.showModal = true;
      this.$nextTick(() => {
        this.editLoading = null;
      });
    },
    async saveComments() {
      if (!this.isFormValid) {
        this.$bvToast.toast("Please enter comment", {
          title: "Validation Error",
          variant: "warning",
          solid: true,
        });
        return;
      }

      this.saving = true;
      try {
        this.form.remarks = this.form.remarks.trim();

        if (this.showQuoteField) {
          this.form.quote = (this.form.quote || "").trim();
        }

        const userID =
          this.$store.state.userData.userID ||
          this.$store.state.userData.userId ||
          this.$store.state.userData.id ||
          0;

        const obj = {
          url:
            this.$store.state.domain +
            "RemarksExtra/Save?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId +
            "&userID=" +
            userID,
          body: this.form,
          token: this.$store.state.userData.token,
          message: this.isEdit
            ? "Comment updated successfully"
            : "Comment added successfully",
          context: this,
        };

        const success = await this.post(obj);
        if (success) {
          this.commentsCache = {};
          this.showModal = false;
          this.loadComments();
        }
      } catch (error) {
        this.$bvToast.toast("Failed to save comment", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.saving = false;
      }
    },
    async deleteComment(item) {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        this.deleteLoading = item.id;
        try {
          const userID =
            this.$store.state.userData.userID ||
            this.$store.state.userData.userId ||
            this.$store.state.userData.id ||
            0;

          const obj = {
            url:
              this.$store.state.domain +
              "RemarksExtra/Delete?" +
              "db=" +
              encodeURIComponent(this.$store.state.userData.db) +
              "&campusID=" +
              this.$store.state.userData.cId +
              "&id=" +
              item.id +
              "&userID=" +
              userID,
            headers: {
              db: this.$store.state.userData.db,
              userID: userID,
            },
            token: this.$store.state.userData.token,
            message: "Comment deleted successfully",
            context: this,
          };

          const success = await this.delete(obj);
          if (success) {
            this.commentsCache = {};
            this.loadComments();
          }
        } catch (error) {
          this.$bvToast.toast("Failed to delete comment", {
            title: "Error",
            variant: "danger",
            solid: true,
          });
        } finally {
          this.deleteLoading = null;
        }
      }
    },
    resetForm() {
      this.form = {
        id: 0,
        remarks: "",
        quote: "",
        examID: 0,
        examIDStr: "",
        gender: "",
      };
      this.isEdit = false;
    },
    openImportModal() {
      this.showImportModal = true;
      this.importMode = "new";
      this.resetImportModal();
    },
    resetImportModal() {
      this.importFile = null;
      this.importFileState = null;
      this.importPreview = [];
      this.totalImportRecords = 0;
      this.newCount = 0;
      this.updateCount = 0;
      this.importMode = "new";
      if (this.$refs.importFile) {
        this.$refs.importFile.reset();
      }
    },
    getColumnTooltip() {
      const quoteColumn = this.showQuoteField
        ? `<code>quote</code> - Quote text (optional)<br/>`
        : "";

      if (this.importMode === "new") {
        const columns = this.showQuoteField
          ? `<code>quote</code> and <code>remarks</code>`
          : `<code>remarks</code>`;
        return `
          <div style="text-align: left; padding: 0.5rem;">
            <strong>Required Column:</strong><br/>
            ${quoteColumn}
            <code>remarks</code> - Comment text (required)<br/><br/>
            <strong>Example columns:</strong><br/>
            ${columns}
          </div>
        `;
      }

      const updateColumns = this.showQuoteField
        ? `<code>id</code>, <code>quote</code>, <code>remarks</code>`
        : `<code>id</code>, <code>remarks</code>`;

      return `
        <div style="text-align: left; padding: 0.5rem;">
          <strong>Required Columns:</strong><br/>
          <code>id</code> - Comment ID (0 or empty for new, existing ID to update)<br/>
          ${quoteColumn}
          <code>remarks</code> - Comment text (required)<br/><br/>
          <strong>Example columns:</strong><br/>
          ${updateColumns}
        </div>
      `;
    },
    getImportColumnIndexes(headers) {
      const idIndex = headers.findIndex((h) => h === "id");
      const remarksIndex = headers.findIndex(
        (h) =>
          h === "remarks" ||
          h === "remark" ||
          h === "comment" ||
          h === "comments"
      );
      const quoteIndex = this.showQuoteField
        ? headers.findIndex((h) => h === "quote" || h === "quotes")
        : -1;

      return { idIndex, remarksIndex, quoteIndex };
    },
    buildImportRow(row, indexes) {
      const { idIndex, remarksIndex, quoteIndex } = indexes;
      const id = idIndex !== -1 ? row[idIndex] || 0 : 0;
      const remarks = row[remarksIndex] || "";
      const quote =
        quoteIndex !== -1 && row[quoteIndex] != null
          ? row[quoteIndex].toString().trim()
          : "";

      if (!remarks || !remarks.toString().trim()) {
        return null;
      }

      const item = {
        id: idIndex !== -1 ? parseInt(id) || 0 : 0,
        remarks: remarks.toString().trim(),
      };

      if (this.showQuoteField) {
        item.quote = quote;
      }

      return item;
    },
    onFileSelected(event) {
      const file = event.target.files?.[0] || this.importFile;
      if (!file) {
        this.importFileState = null;
        this.importPreview = [];
        return;
      }

      const allowedExtensions = [".xlsx", ".xls"];
      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf("."));

      if (allowedExtensions.includes(fileExtension)) {
        this.importFileState = true;
        this.parseImportFile(file);
      } else {
        this.importFileState = false;
        this.importPreview = [];
        this.$bvToast.toast("Please select a valid Excel file (.xlsx, .xls)", {
          title: "Invalid File",
          variant: "danger",
          solid: true,
        });
      }
    },
    async parseImportFile(file) {
      try {
        const rows = await readXlsxFile(file);

        if (rows.length < 2) {
          this.$bvToast.toast(
            "Excel file must have at least a header row and one data row",
            {
              title: "Invalid File",
              variant: "warning",
              solid: true,
            }
          );
          this.importPreview = [];
          return;
        }

        // Get headers (first row)
        const headers = rows[0].map((h) =>
          (h || "").toString().toLowerCase().trim()
        );
        const indexes = this.getImportColumnIndexes(headers);
        const { idIndex, remarksIndex } = indexes;

        if (remarksIndex === -1) {
          this.$bvToast.toast('Excel file must contain a "remarks" column', {
            title: "Invalid Format",
            variant: "warning",
            solid: true,
          });
          this.importPreview = [];
          return;
        }

        // Determine import mode based on whether id column exists
        this.importMode = idIndex !== -1 ? "update" : "new";

        // Parse data rows
        const preview = [];
        for (let i = 1; i < rows.length; i++) {
          const item = this.buildImportRow(rows[i], indexes);
          if (item) {
            preview.push(item);
          }
        }

        // Calculate counts
        this.totalImportRecords = preview.length;
        this.newCount = preview.filter((p) => p.id === 0 || !p.id).length;
        this.updateCount = preview.filter((p) => p.id > 0).length;

        // Show preview (first 20 records)
        this.importPreview = preview.slice(0, 20);

        if (preview.length > 20) {
          this.$bvToast.toast(
            `Preview showing first 20 of ${preview.length} records`,
            {
              title: "Preview",
              variant: "info",
              solid: true,
              toaster: "b-toaster-top-center",
              autoHideDelay: 3000,
            }
          );
        }
      } catch (error) {
        console.error("Error parsing file:", error);
        this.$bvToast.toast(
          "Error reading Excel file. Please check the file format.",
          {
            title: "Error",
            variant: "danger",
            solid: true,
          }
        );
        this.importPreview = [];
      }
    },
    async processImport() {
      if (!this.importFile || this.importPreview.length === 0) {
        this.$bvToast.toast("Please select a valid file", {
          title: "Validation Error",
          variant: "warning",
          solid: true,
        });
        return;
      }

      this.importing = true;
      try {
        // Re-parse to get all data (not just preview)
        const rows = await readXlsxFile(this.importFile);
        const headers = rows[0].map((h) =>
          (h || "").toString().toLowerCase().trim()
        );
        const indexes = this.getImportColumnIndexes(headers);

        const importData = [];
        for (let i = 1; i < rows.length; i++) {
          const item = this.buildImportRow(rows[i], indexes);
          if (item) {
            importData.push(item);
          }
        }

        if (importData.length === 0) {
          this.$bvToast.toast("No valid data found in the file", {
            title: "Validation Error",
            variant: "warning",
            solid: true,
          });
          this.importing = false;
          return;
        }

        const userID =
          this.$store.state.userData.userID ||
          this.$store.state.userData.userId ||
          this.$store.state.userData.id ||
          0;

        const obj = {
          url:
            this.$store.state.domain +
            "RemarksExtra/Import?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId +
            "&userID=" +
            userID,
          body: importData,
          token: this.$store.state.userData.token,
          message: `Successfully imported ${importData.length} comment(s)`,
          context: this,
        };

        const response = await this.post(obj);

        if (response) {
          this.commentsCache = {};
          this.showImportModal = false;
          this.loadComments();

          // Show detailed results if available
          if (
            response.addedCount !== undefined ||
            response.updatedCount !== undefined
          ) {
            const added = response.addedCount || 0;
            const updated = response.updatedCount || 0;
            let message = "";
            if (added > 0 && updated > 0) {
              message = `${added} new comment(s) added and ${updated} comment(s) updated`;
            } else if (added > 0) {
              message = `${added} new comment(s) added`;
            } else if (updated > 0) {
              message = `${updated} comment(s) updated`;
            }
            if (message) {
              this.$bvToast.toast(message, {
                title: "Import Success",
                variant: "success",
                solid: true,
              });
            }
          }
        }
      } catch (error) {
        console.error("Import error:", error);
        this.$bvToast.toast("Failed to import comments", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.importing = false;
      }
    },
    async exportComments() {
      this.exporting = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "RemarksExtra/Export?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };

        const response = await this.get(obj);

        let data = [];
        if (response && response.data && Array.isArray(response.data)) {
          data = response.data;
        } else if (Array.isArray(response)) {
          data = response;
        }

        // Map export columns
        const exportData = data.map((item) => {
          const row = {
            id: item.id || 0,
          };

          if (this.showQuoteField) {
            row.quote = item.quote || "";
          }

          row.remarks = item.remarks || "";
          return row;
        });

        // Create workbook
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Comments");

        // Generate filename with timestamp
        const timestamp = new Date()
          .toISOString()
          .slice(0, 10)
          .replace(/-/g, "");
        const filename = `comments_export_${timestamp}.xlsx`;

        // Download file
        XLSX.writeFile(wb, filename);

        this.$bvToast.toast(`Exported ${exportData.length} comment(s)`, {
          title: "Export Success",
          variant: "success",
          solid: true,
        });
      } catch (error) {
        console.error("Export error:", error);
        this.$bvToast.toast("Failed to export comments", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";

/* Keyframe animations - Soft landing */
@keyframes slideUpFade {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.filter-row-single-line {
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-left: -0.25rem;
  margin-right: -0.25rem;
  align-items: center;
  background: transparent !important;

  @media (min-width: 1200px) {
    flex-wrap: nowrap;
  }

  > [class*="col-"] {
    padding-left: 0.375rem;
    padding-right: 0.375rem;
    display: flex;
    align-items: center;
    background: transparent !important;
  }

  // Ensure buttons column is right-aligned
  > [class*="col-"].text-right {
    justify-content: flex-end;
  }
}

// Filter Control - Consistent height and styling
.filter-control {
  height: 42px !important;
  min-height: 42px !important;
  border-radius: 10px;
  border: 1.5px solid #e0d8ff;
  padding: 0.5rem 0.75rem;
  background: #fff;
  transition: all 0.2s ease;
  color: #2d1b69;
  font-weight: 500;
  width: 100%;

  &:hover {
    border-color: #b8a9ff;
  }

  &:focus {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
    outline: none;
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }
}

// Action Button Filter
.action-btn-filter {
  height: 42px;
  font-weight: 600;
  border-radius: 10px;
  padding: 0.5rem 1rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.gap-2 {
  gap: 0.5rem;
}

// Comments List Card
.comments-list-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem 1.75rem 1.5rem 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 1.5rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: visible; // Changed from hidden to visible to allow dropdowns to show properly
  animation: slideUpFade 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  z-index: 1;

  // Gradient Line Wrapper - Aligned with content, shorter length
  .gradient-line-wrapper {
    position: absolute;
    top: 0;
    left: 0.75rem; // Reduced margin to touch curve
    right: 0.75rem; // Reduced margin to touch curve
    width: calc(100% - 1.5rem); // Subtract left and right padding
    height: 5px;
    overflow: hidden;
    border-radius: 10px 10px 0 0;
    z-index: 1;
    pointer-events: none;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 5px;
      background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
      border-radius: 10px 10px 0 0;
    }
  }

  &:hover {
    box-shadow: 0 15px 40px rgba(115, 103, 240, 0.2);
    transform: translateY(-2px);
  }

  // Filter row styling inside card
  .filter-row-single-line {
    background: transparent !important;
    padding: 0;
    margin-bottom: 1rem;
  }
}

.table-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0edff;
  margin-bottom: 1rem;

  h5 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
  }

  small {
    font-size: 0.85rem;
    color: #6b7280;
    font-weight: 500;
  }
}

.comment-title {
  font-size: 1.25rem;
  font-weight: 800;
  color: #2d1b69;
  letter-spacing: -0.3px;
}

// Pagination Controls - Top (Compact Single Line)
.pagination-controls-top {
  padding: 0.75rem 1.25rem;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  gap: 1rem;
  position: relative;

  @media (max-width: 992px) {
    flex-wrap: wrap;
    gap: 0.75rem;

    .pagination-count-display {
      order: 3;
      width: 100%;
      margin: 0.5rem 0 0 0;
      text-align: center;
    }
  }

  > .d-flex {
    gap: 0.5rem;
    flex-wrap: nowrap;
    align-items: center;
    flex-shrink: 0;
  }

  > div {
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }
}

// Page Size Wrapper
.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;

  .page-size-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
    white-space: nowrap;
  }
}

// Compact Page Size Select
.page-size-select-compact {
  min-width: 75px;
  width: 75px;

  ::v-deep .vs__dropdown-toggle {
    height: 32px !important;
    min-height: 32px !important;
    border-radius: 6px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0.25rem 0.5rem !important;
    background: #fff !important;
    font-size: 0.8125rem !important;

    &:hover {
      border-color: #b8a9ff !important;
    }

    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 2px rgba(124, 93, 250, 0.1) !important;
    }
  }

  ::v-deep .vs__selected {
    margin: 0 !important;
    padding: 0 !important;
    color: #2d1b69 !important;
    font-weight: 600 !important;
    font-size: 0.8125rem !important;
    line-height: 1.5 !important;
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  ::v-deep .vs__selected-options {
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
  }

  ::v-deep .vs__search {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  ::v-deep .vs__actions {
    padding: 0 0.25rem 0 0.5rem !important;
  }

  ::v-deep .vs__dropdown-menu {
    z-index: 99999 !important;
    position: fixed !important;
    min-width: 75px !important;
    width: 75px !important;
    border-radius: 8px !important;
    border: 1.5px solid #e0d8ff !important;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15) !important;
    margin-top: 4px !important;
    background: #ffffff !important;
    overflow: visible !important;
    max-height: 300px !important;
    overflow-y: auto !important;
  }

  ::v-deep .vs__dropdown-option {
    padding: 0.5rem 0.75rem !important;
    color: #2d1b69 !important;
    font-weight: 500 !important;
    font-size: 0.8125rem !important;
    background-color: #ffffff !important;
    background-image: none !important;
    opacity: 1 !important;
    visibility: visible !important;
    cursor: pointer !important;
    transition: background-color 0.15s ease !important;

    &:hover {
      background-color: #f9fafb !important;
      background-image: none !important;
      color: #1f2937 !important;
    }

    &--highlight,
    &--selected {
      background-color: #f3f4f6 !important;
      background-image: none !important;
      color: #1f2937 !important;
      font-weight: 600 !important;
    }

    &:active {
      background-color: #e5e7eb !important;
    }

    &--selected {
      background-color: #f3f0ff !important;
      color: #7c5dfa !important;
      font-weight: 600 !important;
    }
  }
}

// Pagination Count Display
.pagination-count-display {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  margin: 0 1rem;
  min-width: 200px;

  .count-text {
    font-size: 0.8125rem;
    color: #64748b;
    font-weight: 500;
    white-space: nowrap;

    strong {
      color: #2d1b69;
      font-weight: 700;
    }
  }

  @media (max-width: 992px) {
    margin: 0;
    min-width: auto;
    width: 100%;
  }
}

// Compact Pagination Switch
.pagination-switch-compact {
  margin-bottom: 0 !important;
  display: flex;
  align-items: center;
  white-space: nowrap;

  ::v-deep .custom-control-label {
    padding-left: 0.375rem;
    font-size: 0.8125rem;
    color: #64748b;
    cursor: pointer;
    font-weight: 500;
    margin-bottom: 0;
    white-space: nowrap;
  }

  ::v-deep .custom-control-input:checked ~ .custom-control-label {
    color: #7c5dfa;
  }

  ::v-deep .custom-switch .custom-control-label::before {
    width: 1.75rem;
    height: 1rem;
    border-radius: 0.5rem;
    background-color: #cbd5e1;
    border-color: #cbd5e1;
  }

  ::v-deep .custom-switch .custom-control-label::after {
    width: calc(1rem - 4px);
    height: calc(1rem - 4px);
    border-radius: 50%;
    background-color: #fff;
  }

  ::v-deep
    .custom-switch
    .custom-control-input:checked
    ~ .custom-control-label::before {
    background-color: #7c5dfa;
    border-color: #7c5dfa;
  }

  ::v-deep
    .custom-switch
    .custom-control-input:checked
    ~ .custom-control-label::after {
    transform: translateX(0.75rem);
  }
}

// Compact Pagination Wrapper
.pagination-wrapper-compact {
  display: flex;
  align-items: center;

  ::v-deep .pagination {
    margin-bottom: 0;

    .page-link {
      padding: 0.375rem 0.625rem;
      font-size: 0.8125rem;
      line-height: 1.5;
      border-radius: 6px;
      margin: 0 0.125rem;
      border: 1px solid #e0d8ff;
      color: #64748b;
      font-weight: 500;
      min-width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background: #f3f0ff;
        border-color: #b8a9ff;
        color: #7c5dfa;
      }
    }

    .page-item.active .page-link {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      border-color: #7c5dfa;
      color: #fff;
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(124, 93, 250, 0.3);
    }

    .page-item.disabled .page-link {
      background: #f8fafc;
      border-color: #e0d8ff;
      color: #cbd5e1;
      cursor: not-allowed;
    }
  }
}

// Comments Table - Modern Compact Design
.comments-table {
  margin-top: 1rem;
  border-radius: 12px;
  // overflow: hidden;
  border: 1px solid #e5e7eb;

  ::v-deep th {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: none;
    font-weight: 700;
    color: #1e293b;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    padding: 0.75rem 0.875rem;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 5;

    &::after {
      display: none;
    }
  }

  ::v-deep td {
    vertical-align: top;
    border-top: 1px solid #f1f5f9;
    border-bottom: 1px solid #f8fafc;
    padding: 0.75rem 0.875rem;
    background: #ffffff;
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    font-size: 0.875rem;
    color: #475569;

    &.comment-cell {
      vertical-align: top !important;
      white-space: normal !important;
      word-break: break-word !important;
      word-wrap: break-word !important;
      max-width: none !important;
      min-width: 400px !important;
      line-height: 1.6 !important;
    }
  }

  ::v-deep tbody tr {
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative;
    border-left: 2px solid transparent;

    &:nth-child(even) {
      background: #f8fafc;

      td {
        background: #f8fafc;
      }
    }

    &:nth-child(odd) {
      background: #ffffff;

      td {
        background: #ffffff;
      }
    }

    &:hover {
      background: #f1f5f9 !important;
      border-left-color: #7c5dfa;
      box-shadow: -2px 0 8px rgba(124, 93, 250, 0.1);
      z-index: 1;
      position: relative;

      td {
        background: transparent !important;
        color: #1e293b;
        font-weight: 500;
      }
    }

    &:active {
      transform: none;
    }
  }
}

// Skeleton Loader Styles - Premium Animated Effect
.skeleton-row {
  animation: skeletonPulse 2s ease-in-out infinite;
  transition: opacity 0.3s ease;

  &:nth-child(1) {
    animation-delay: 0s;
  }

  &:nth-child(2) {
    animation-delay: 0.15s;
  }

  &:nth-child(3) {
    animation-delay: 0.3s;
  }

  &:nth-child(4) {
    animation-delay: 0.45s;
  }

  &:nth-child(5) {
    animation-delay: 0.6s;
  }

  &:hover {
    background: transparent !important;
    border-left-color: transparent !important;
    box-shadow: none !important;
    transform: none !important;

    td {
      background: transparent !important;
      color: inherit !important;
      font-weight: normal !important;
    }
  }
}

.skeleton-cell {
  padding: 0.875rem 0.875rem !important;
  vertical-align: middle !important;
  border-top: 1px solid #f1f5f9 !important;
  border-bottom: 1px solid #f8fafc !important;
  background: #ffffff !important;
  position: relative;

  &:nth-child(even) {
    background: #fafbfc !important;
  }

  // Add subtle border animation
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 4px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(124, 93, 250, 0.03) 50%,
      transparent 100%
    );
    animation: skeletonBorderGlow 2.5s ease-in-out infinite;
    pointer-events: none;
  }
}

.skeleton-loader {
  height: 18px;
  border-radius: 10px;
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #f8f8f8 25%,
    #f0f0f0 50%,
    #f8f8f8 75%,
    #f0f0f0 100%
  );
  background-size: 300% 100%;
  animation: skeletonShimmer 2s ease-in-out infinite;
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  // Premium shimmer effect
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.6) 50%,
      transparent 100%
    );
    animation: skeletonShine 2s ease-in-out infinite;
    filter: blur(8px);
  }

  // Secondary glow layer
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(124, 93, 250, 0.08) 30%,
      rgba(157, 122, 255, 0.12) 50%,
      rgba(124, 93, 250, 0.08) 70%,
      transparent 100%
    );
    animation: skeletonColorWave 2.5s ease-in-out infinite;
    border-radius: 10px;
  }
}

// Different widths for different column positions
.skeleton-cell {
  &:nth-child(1) .skeleton-loader {
    width: 50px;
    margin: 0 auto;
  }

  &:nth-child(2) .skeleton-loader {
    width: 70px;
  }

  &:nth-child(3) .skeleton-loader {
    width: 140px;
  }

  &:nth-child(4) .skeleton-loader {
    width: 110px;
  }

  &:nth-child(5) .skeleton-loader {
    width: 100px;
  }

  &:nth-child(6) .skeleton-loader {
    width: 100px;
  }

  &:nth-child(7) .skeleton-loader {
    width: 60px;
  }

  &:nth-child(8) .skeleton-loader {
    width: 90px;
  }

  &:nth-child(9) .skeleton-loader {
    width: 180px;
  }

  &:nth-child(10) .skeleton-loader {
    width: 180px;
  }

  &:nth-child(11) .skeleton-loader {
    width: 120px;
  }

  &:nth-child(12) .skeleton-loader {
    width: 100px;
  }
}

@keyframes skeletonShimmer {
  0% {
    background-position: -300% 0;
  }
  100% {
    background-position: 300% 0;
  }
}

@keyframes skeletonShine {
  0% {
    left: -100%;
  }
  50% {
    left: 100%;
  }
  100% {
    left: 100%;
  }
}

@keyframes skeletonColorWave {
  0%,
  100% {
    opacity: 0.3;
    transform: scaleX(0.8);
  }
  50% {
    opacity: 0.6;
    transform: scaleX(1);
  }
}

@keyframes skeletonBorderGlow {
  0%,
  100% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
}

@keyframes skeletonPulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.85;
    transform: scale(0.998);
  }
}

.comment-cell {
  white-space: normal !important;
  word-break: break-word !important;
  word-wrap: break-word !important;
  vertical-align: top !important;
  max-width: none !important;
  min-width: 400px !important;
  padding: 0.75rem 1rem !important;
  line-height: 1.6 !important;
}

.actions-cell {
  min-width: 100px;
  max-width: 150px;
  vertical-align: middle !important;
}

// Action Buttons
.action-btn-table {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

.action-btn-outline {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-weight: 600;
  background: #ffffff !important;
  border: 2px solid #ef4444 !important;
  color: #ef4444 !important;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.15);

  &:hover:not(:disabled) {
    background: #fef2f2 !important;
    border-color: #dc2626 !important;
    color: #dc2626 !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

@media (max-width: 900px) {
  .comment-cell {
    min-width: 180px !important;
    max-width: 300px !important;
  }
  .actions-cell {
    min-width: 80px !important;
    max-width: 120px !important;
  }
}

// Modern Modal Styling
::v-deep .modal {
  z-index: 1050;
}

::v-deep .modal-backdrop {
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
}

::v-deep .modal-dialog {
  overflow: visible !important;
}

::v-deep .modal-content {
  border: none;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  overflow: visible;
  position: relative;
}

::v-deep .modal-header {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border-bottom: 2px solid #e0d8ff;
  padding: 1.5rem 4rem 1.5rem 2rem !important;
  border-radius: 20px 20px 0 0;
  position: relative !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  overflow: visible !important;
  min-height: 4.5rem !important;
  flex-direction: row !important;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
    z-index: 1;
  }

  .modal-title {
    font-size: 1.5rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
    position: relative;
    z-index: 2;
    flex: 1;
    padding-right: 1.5rem;
    margin-right: 0.5rem;
    order: 1 !important;
    text-align: left !important;
    margin-left: 0 !important;
  }

  // Force close button to be positioned absolutely on the right
  > button:first-child:not(.modal-title),
  > .close:first-child {
    order: 999 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
}

// Modern Close Button - Revamped Design
// Target ALL possible close button selectors with maximum specificity
// Override global modal styles that position close button on the left
// Using multiple selectors to ensure highest specificity
.remarks-modal .modal-header .close,
.remarks-modal .modal-header button.close,
.remarks-modal .modal-header .btn-close,
.remarks-modal .modal-header button[aria-label="Close"],
.remarks-modal .modal-header .modal-header-close,
.remarks-modal .modal-header button[type="button"].close,
.remarks-modal .modal-header > button:first-child:not(.modal-title),
.remarks-modal .modal-header > .close:first-child,
::v-deep .remarks-modal .modal-header .close,
::v-deep .remarks-modal .modal-header button.close,
::v-deep .remarks-modal .modal-header .btn-close,
::v-deep .remarks-modal .modal-header button[aria-label="Close"],
::v-deep .remarks-modal .modal-header .modal-header-close,
::v-deep .remarks-modal .modal-header button[type="button"].close,
::v-deep .remarks-modal .modal-header > button:first-child:not(.modal-title),
::v-deep .remarks-modal .modal-header > .close:first-child,
::v-deep .modal .modal-header .close,
::v-deep .modal .modal-header button.close,
::v-deep .modal .modal-header .btn-close,
::v-deep .modal .modal-header button[aria-label="Close"],
::v-deep .modal .modal-header .modal-header-close,
::v-deep .modal .modal-header button[type="button"].close,
::v-deep .modal .modal-header > button:first-child:not(.modal-title),
::v-deep .modal .modal-header > .close:first-child,
::v-deep .modal-header .close,
::v-deep .modal-header button.close,
::v-deep .modal-header .btn-close,
::v-deep .modal-header button[aria-label="Close"],
::v-deep .modal-header .modal-header-close,
::v-deep .modal-header button[type="button"].close,
::v-deep .modal-header > button:first-child:not(.modal-title),
::v-deep .modal-header > .close:first-child {
  // Reset ALL positioning from global styles
  margin: 0 !important;
  padding: 0 !important;

  // Override global transform that moves button to left
  // Don't reset transform here, we'll set it below for positioning

  // Modern button dimensions
  width: 2.75rem !important;
  height: 2.75rem !important;
  min-width: 2.75rem !important;
  min-height: 2.75rem !important;
  max-width: 2.75rem !important;
  max-height: 2.75rem !important;

  // Flexbox centering
  display: flex !important;
  align-items: center !important;

  // CRITICAL: Absolute positioning on the RIGHT side
  right: 1.5rem !important;
  transform: translateY(-50%) !important;
  left: auto !important;

  // Modern styling
  border-radius: 12px !important;
  background: linear-gradient(135deg, #ffffff 0%, #f8f7ff 100%) !important;
  border: 2px solid #e8e4ff !important;
  box-shadow: 0 2px 8px rgba(124, 93, 250, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05) !important;

  // Smooth transitions
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  cursor: pointer !important;

  // Remove default button styles
  float: none !important;
  order: 999 !important;

  // Icon/X styling
  span,
  &::before,
  &::after {
    font-size: 1.25rem !important;
    line-height: 1 !important;
    color: #7c5dfa !important;
    font-weight: 600 !important;
    display: block !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }

  // Hover state - Enhanced
  &:hover {
    background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%) !important;
    border-color: #7c5dfa !important;
    box-shadow: 0 4px 16px rgba(124, 93, 250, 0.25),
      0 2px 8px rgba(124, 93, 250, 0.15) !important;
    transform: translateY(-50%) translateY(-2px) scale(1.05) !important;
    right: 1.5rem !important;
    left: auto !important;

    span,
    &::before,
    &::after {
      color: #ffffff !important;
      transform: rotate(90deg) !important;
    }
  }

  // Active state
  &:active {
    transform: translateY(-50%) scale(0.98) !important;
    box-shadow: 0 2px 8px rgba(124, 93, 250, 0.2) !important;
    right: 1.5rem !important;
    left: auto !important;
  }

  // Focus state - Accessibility
  &:focus {
    outline: none !important;
    box-shadow: 0 0 0 4px rgba(124, 93, 250, 0.15),
      0 4px 16px rgba(124, 93, 250, 0.25) !important;
    transform: translateY(-50%) !important;
    right: 1.5rem !important;
    left: auto !important;
  }

  // Focus-visible for keyboard navigation
  &:focus-visible {
    outline: 2px solid #7c5dfa !important;
    outline-offset: 2px !important;
  }
}

// Smooth entrance animation for close button when modal opens
::v-deep .modal.show .modal-header .close,
::v-deep .modal.show .modal-header button.close,
::v-deep .modal.show .modal-header .btn-close {
  animation: closeButtonFadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.2s both !important;
}

@keyframes closeButtonFadeIn {
  0% {
    opacity: 0;
    transform: translateX(10px) scale(0.8);
  }
  100% {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

// Exclude modal-title from button styling
::v-deep .modal-header .modal-title {
  position: relative !important;
  left: auto !important;
  right: auto !important;
  top: auto !important;
  margin: 0 !important;
  padding: 0 !important;
  width: auto !important;
  height: auto !important;
  min-width: auto !important;
  min-height: auto !important;
  float: none !important;
  order: 1 !important;
}

::v-deep .modal-body {
  padding: 2rem;
  background: #ffffff;
}

::v-deep .import-modal .modal-body {
  padding: 1.25rem;
}

::v-deep .modal-footer {
  background: #faf9ff;
  border-top: 1px solid #e0d8ff;
  padding: 1.25rem 2rem;
  border-radius: 0 0 20px 20px;

  .btn {
    border-radius: 10px;
    font-weight: 600;
    padding: 0.625rem 1.25rem;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }
}

.modern-textarea {
  border-radius: 10px;
  border: 1.5px solid #e0d8ff;
  padding: 0.75rem 0.875rem;
  background: #fff;
  transition: all 0.2s ease;
  color: #1e293b;
  font-weight: 500;
  resize: vertical;

  &:hover {
    border-color: #b8a9ff;
  }

  &:focus {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
    outline: none;
  }

  &::placeholder {
    color: #94a3b8;
    font-weight: 400;
  }
}

.btn-modern-cancel {
  border-radius: 10px;
  font-weight: 600;
  padding: 0.625rem 1.5rem;
  border: 1.5px solid #e0d8ff;
  color: #64748b;
  background: #fff;
  transition: all 0.3s ease;

  &:hover {
    background: #f8fafc;
    border-color: #b8a9ff;
    color: #475569;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
}

.btn-modern-primary {
  border-radius: 10px;
  font-weight: 600;
  padding: 0.625rem 1.5rem;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  border: none;
  color: #fff;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #6d4cdb 0%, #8b6fff 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

// Import Modal Styles
.import-instructions-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.instruction-card {
  border-radius: 8px;
  padding: 0.875rem 1rem;
  border: 1px solid;
  transition: all 0.2s ease;
  background: #ffffff;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

.instruction-header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.instruction-icon {
  margin-top: 0.125rem;
  flex-shrink: 0;
}

.instruction-content {
  flex: 1;
  min-width: 0;
}

.instruction-title {
  display: flex;
  align-items: center;
  margin-bottom: 0.375rem;

  strong {
    font-size: 0.875rem;
    font-weight: 600;
    color: #1e293b;
  }

  .tooltip-icon {
    cursor: help;
    opacity: 0.7;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
    }
  }
}

.instruction-text {
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #475569;

  code {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.125rem 0.375rem;
    border-radius: 4px;
    margin: 0 0.125rem;
    font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
  }

  .instruction-detail {
    display: block;
    margin-top: 0.25rem;
    font-size: 0.75rem;
    color: #64748b;
    font-style: italic;
  }
}

.instruction-new {
  border-color: #10b981;
  background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%);

  .instruction-icon {
    color: #10b981;
  }

  .instruction-title strong {
    color: #065f46;
  }

  code {
    background: #10b981;
    color: #ffffff;
  }
}

.instruction-update {
  border-color: #f59e0b;
  background: linear-gradient(135deg, #fffbeb 0%, #ffffff 100%);

  .instruction-icon {
    color: #f59e0b;
  }

  .instruction-title strong {
    color: #92400e;
  }

  code {
    background: #f59e0b;
    color: #1e293b;
  }
}

.import-compact-info {
  padding: 0.75rem;
  background: #faf9ff;
  border-radius: 8px;
  border: 1px solid #e0d8ff;

  strong {
    font-size: 0.875rem;
  }
}

.import-mode-badge-sm {
  font-size: 0.6875rem;
  padding: 0.25rem 0.5rem;
  font-weight: 600;
  border-radius: 4px;
}

.badge-sm {
  font-size: 0.6875rem;
  padding: 0.25rem 0.5rem;
}

.import-type-card {
  padding: 1.25rem;
  background: linear-gradient(135deg, #faf9ff 0%, #ffffff 100%);
  border-radius: 12px;
  border: 2px solid #e0d8ff;
  box-shadow: 0 2px 8px rgba(124, 93, 250, 0.08);

  h6 {
    font-size: 1.125rem;
    font-weight: 700;
    color: #2d1b69;
    display: flex;
    align-items: center;
  }
}

.import-mode-badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
  font-weight: 600;
  border-radius: 6px;
}

.column-format-guide {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e0d8ff;

  strong {
    color: #7c5dfa;
    font-size: 0.9375rem;
  }
}

.column-examples {
  margin-top: 0.75rem;
}

.example-box {
  background: #ffffff;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  border: 1.5px solid;
  transition: all 0.3s ease;

  &.example-new {
    border-color: #10b981;
    background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%);

    &:hover {
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
    }
  }

  &.example-update {
    border-color: #f59e0b;
    background: linear-gradient(135deg, #fffbeb 0%, #ffffff 100%);

    &:hover {
      box-shadow: 0 4px 12px rgba(245, 158, 11, 0.15);
    }
  }
}

.example-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
  color: #1e293b;

  strong {
    color: #1e293b;
    font-weight: 600;
  }
}

.example-table {
  overflow-x: auto;

  table {
    font-size: 0.8125rem;
    margin-bottom: 0;

    th {
      font-weight: 600;
      padding: 0.5rem;
      text-align: center;
      white-space: nowrap;
    }

    td {
      padding: 0.5rem;
      text-align: left;
      white-space: nowrap;
    }
  }
}

.example-note {
  padding: 0.5rem;
  background: #f8fafc;
  border-radius: 6px;
  border-left: 3px solid #f59e0b;

  small {
    color: #475569;
    font-size: 0.75rem;
    line-height: 1.5;

    code {
      background: #fee2e2;
      color: #dc2626;
      padding: 0.125rem 0.375rem;
      border-radius: 4px;
      font-size: 0.6875rem;
      font-weight: 600;
    }
  }
}

.import-header {
  padding: 1rem;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border-radius: 12px;
  border: 1px solid #e0d8ff;

  h6 {
    font-size: 1.125rem;
    font-weight: 700;
    color: #2d1b69;
    display: flex;
    align-items: center;
  }
}

.import-info-box {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 0.75rem;

  p {
    margin-bottom: 0.5rem;
    color: #1e293b;
    font-size: 0.875rem;
  }

  ul {
    margin: 0;
    padding-left: 1.25rem;
    color: #475569;
    font-size: 0.8125rem;
    line-height: 1.6;

    li {
      margin-bottom: 0.25rem;
    }

    code {
      background: #e0e7ff;
      color: #7c5dfa;
      padding: 0.125rem 0.375rem;
      border-radius: 4px;
      font-size: 0.75rem;
      font-weight: 600;
    }
  }
}

.modern-file-input {
  ::v-deep .custom-file-label {
    border-radius: 10px;
    border: 1.5px solid #e0d8ff;
    padding: 0.75rem 0.875rem;
    background: #fff;
    transition: all 0.2s ease;
    color: #1e293b;
    font-weight: 500;

    &::after {
      border-radius: 0 10px 10px 0;
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      color: #fff;
      font-weight: 600;
      padding: 0.75rem 1.25rem;
    }
  }

  ::v-deep .custom-file-input:focus ~ .custom-file-label {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
  }

  ::v-deep .custom-file-input:valid ~ .custom-file-label {
    border-color: #10b981;
  }
}

.import-preview {
  border: 1px solid #e0d8ff;
  border-radius: 8px;
  padding: 0.75rem;
  background: #faf9ff;

  h6 {
    font-size: 0.9375rem;
    font-weight: 700;
    color: #2d1b69;
    margin-bottom: 0.75rem;
  }
}

.preview-table-wrapper {
  max-height: 200px;
  overflow-y: auto;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: #fff;

  .type-badge {
    display: inline-flex;
    align-items: center;
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    font-weight: 600;
    border-radius: 4px;
  }

  .remarks-preview {
    max-width: 400px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
    color: #1e293b;
    font-size: 0.875rem;
  }
}

.preview-table {
  margin-bottom: 0;

  ::v-deep th {
    background: #f8fafc;
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    color: #64748b;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid #e5e7eb;
  }

  ::v-deep td {
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
    color: #475569;
    border-bottom: 1px solid #f1f5f9;
  }

  ::v-deep tbody tr:last-child td {
    border-bottom: none;
  }
}
</style>
